import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Heading,
  Input,
  Spinner,
  Table,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FiArrowLeft, FiLink } from "react-icons/fi"

import {
  OpenAPI,
  type PassportTemplateRead,
  PassportTemplatesService,
} from "@/client"
import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { useOrganization } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/admin-dpp/bindings")({
  component: AdminDppBindings,
})

// The SDK isn't regenerated for the per-tag binding endpoint yet — mirror the
// helper used in admin-dpp.templates.tsx so we can call it directly.
function authedFetch(path: string, init: RequestInit = {}) {
  const token = localStorage.getItem("access_token")
  const base = (OpenAPI.BASE || "").replace(/\/$/, "")
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  })
}

type BindArgs = {
  tagIds: string[]
  templateId: string | null
  // When omitted, each tag keeps its current ``dpp_binding_locked`` value.
  // The bind endpoint always overwrites both fields, so we have to read the
  // current value off the row before POSTing.
  locked?: boolean
  // Used by the per-row locked toggle to send the row's current template_id
  // unchanged. Keyed by tag id → current { template_id, locked }.
  currentByTagId?: Record<
    string,
    { template_id: string | null; locked: boolean }
  >
}

function AdminDppBindings() {
  const sc = useSemanticColors()
  const { t } = useTranslation("dpp")
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const queryClient = useQueryClient()
  const { showToast } = useCustomToast()
  const orgId = currentOrganization?.id

  // ---- server-side search / filter / pagination ----
  // bindingFilter: "" (all) | "unbound" | <PassportTemplate UUID>
  const [bindingFilter, setBindingFilter] = useState<string>("")
  const [searchInput, setSearchInput] = useState<string>("")
  const [search, setSearch] = useState<string>("")
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  // Debounce keystrokes so the API isn't hit on every character.
  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 350)
    return () => window.clearTimeout(id)
  }, [searchInput])
  // Reset to first page whenever the filter changes; otherwise a deep
  // page index could land beyond the new filtered count.
  useEffect(() => {
    setPage(0)
  }, [bindingFilter])

  const tagsQuery = useQuery({
    queryKey: [
      "dpp-bindings-tags",
      orgId,
      page,
      pageSize,
      search,
      bindingFilter,
    ],
    queryFn: async () => {
      // Hand-roll the request — the SDK isn't regenerated for the new
      // dpp_template_id filter on /paginated yet.
      const params = new URLSearchParams({
        skip: String(page * pageSize),
        limit: String(pageSize),
      })
      if (search) params.set("search", search)
      if (bindingFilter) params.set("dpp_template_id", bindingFilter)
      // Pin a deterministic order so binding a tag doesn't reshuffle
      // the visible rows — without an explicit sort the backend returns
      // tags in whatever PostgreSQL hands back, which shifts after each
      // UPDATE on the row.
      params.set("sort_by", "name")
      params.set("sort_order", "asc")
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tags/paginated?${params.toString()}`,
      )
      if (!r.ok) throw new Error(await r.text())
      return (await r.json()) as { data: any[]; count: number }
    },
    enabled: !!orgId,
    staleTime: 30_000,
  })

  const templatesQuery = useQuery({
    queryKey: ["passport-templates", orgId],
    queryFn: () =>
      PassportTemplatesService.listPassportTemplates({
        organisationId: orgId!,
        limit: 500,
      }),
    enabled: !!orgId,
    staleTime: 60_000,
  })

  const templates = useMemo(
    () => templatesQuery.data?.data ?? [],
    [templatesQuery.data?.data],
  )
  const templateMap = useMemo(
    () =>
      new Map<string, PassportTemplateRead>(templates.map((t) => [t.id, t])),
    [templates],
  )

  const rows = tagsQuery.data?.data ?? []
  const totalCount = tagsQuery.data?.count ?? 0
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize))
  const rangeStart = totalCount === 0 ? 0 : page * pageSize + 1
  const rangeEnd = Math.min(totalCount, page * pageSize + rows.length)
  const dppHost = currentOrganization?.dpp_host

  // ---- selection state (mirrors admin-dpp.templates.tsx) ----
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set())
  const [bulkTemplateId, setBulkTemplateId] = useState<string>("")
  // The id of the template most recently picked from a per-row dropdown or
  // bulk Apply. Drives the rules preview pane below the table — display
  // only, cleared when the user unbinds.
  const [previewTemplateId, setPreviewTemplateId] = useState<string | null>(
    null,
  )

  const toggleTagSelection = (id: string, next: boolean) =>
    setSelectedTagIds((prev) => {
      const out = new Set(prev)
      if (next) out.add(id)
      else out.delete(id)
      return out
    })
  const toggleAllSelected = (next: boolean) => {
    if (!next) {
      setSelectedTagIds(new Set())
      return
    }
    setSelectedTagIds(new Set(rows.map((r: any) => r.id)))
  }
  const allSelected = rows.length > 0 && selectedTagIds.size === rows.length

  // ---- bind mutation (handles per-row and bulk; one POST per tag) ----
  const bindMutation = useMutation({
    mutationFn: async ({
      tagIds,
      templateId,
      locked,
      currentByTagId,
    }: BindArgs) => {
      if (!orgId) throw new Error("No organisation selected")
      // No bulk endpoint on the backend — POST one at a time. Small N
      // (operator-driven) so allSettled is fine; we surface a single
      // aggregate toast at the end.
      const results = await Promise.allSettled(
        tagIds.map((tagId) => {
          const current = currentByTagId?.[tagId]
          const payload = {
            template_id: templateId,
            locked: locked ?? current?.locked ?? false,
          }
          return authedFetch(
            `/api/v1/organizations/${orgId}/tags/${tagId}/passport-binding`,
            { method: "POST", body: JSON.stringify(payload) },
          ).then(async (res) => {
            if (!res.ok)
              throw new Error((await res.text()) || `HTTP ${res.status}`)
          })
        }),
      )
      const failed = results.filter((r) => r.status === "rejected").length
      return { total: tagIds.length, failed }
    },
    onSuccess: ({ total, failed }) => {
      queryClient.invalidateQueries({ queryKey: ["dpp-bindings-tags", orgId] })
      setSelectedTagIds(new Set())
      if (failed === 0) {
        showToast(
          t("bindings.toast.successTitle", "Bindings updated"),
          t("bindings.toast.successBody", "{{count}} tag(s) updated.", {
            count: total,
          }),
          "success",
        )
      } else {
        showToast(
          t("bindings.toast.partialTitle", "Some bindings failed"),
          t(
            "bindings.toast.partialBody",
            "{{ok}} of {{total}} tag(s) updated. {{failed}} failed — check permissions or template.",
            { ok: total - failed, total, failed },
          ),
          "error",
        )
      }
    },
    onError: (err: unknown) => {
      showToast(
        t("bindings.toast.errorTitle", "Could not update bindings"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  // ---- inline Tag.sku editor ----
  // Optimistic update so the row doesn't visually flicker between the
  // user's keystrokes and the refetch. The PUT /tags/{id} endpoint
  // accepts a partial TagUpdate which includes ``sku``.
  // SKU catalog drives the per-row pulldown — same source as the
  // /tag-skus and /tag-form pages so an operator picks from canonical
  // values instead of free-typing duplicates.
  const skuCatalogQuery = useQuery({
    queryKey: ["tag-sku-catalog", orgId],
    queryFn: async (): Promise<string[]> => {
      const r = await authedFetch(
        `/api/v1/organizations/${orgId}/tag-skus-catalog/?limit=500`,
      )
      if (!r.ok) return []
      const j = (await r.json()) as { data?: { value: string }[] }
      return (j.data ?? []).map((d) => d.value)
    },
    enabled: !!orgId,
    staleTime: 60_000,
  })
  const skuOptions = skuCatalogQuery.data ?? []

  const updateSkuMutation = useMutation({
    mutationFn: async ({ tagId, sku }: { tagId: string; sku: string }) => {
      const res = await authedFetch(`/api/v1/tags/${tagId}`, {
        method: "PUT",
        body: JSON.stringify({ sku: sku.trim() || null }),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["dpp-bindings-tags", orgId] })
    },
    onError: (err: unknown) => {
      showToast(
        t("bindings.toast.skuErrorTitle", "Could not update SKU"),
        err instanceof Error ? err.message : String(err),
        "error",
      )
    },
  })

  const isBusy = bindMutation.isPending || updateSkuMutation.isPending

  // Build a lookup of the current binding state for each row so the locked
  // toggle can preserve the template_id (the API overwrites both fields).
  const currentByTagId = useMemo(() => {
    const out: Record<string, { template_id: string | null; locked: boolean }> =
      {}
    for (const tag of rows as any[]) {
      out[tag.id] = {
        template_id: tag.dpp_template_id ?? null,
        locked: !!tag.dpp_binding_locked,
      }
    }
    return out
  }, [rows])

  return (
    <Box>
      <Flex mb={4} align="center" gap={3}>
        <VisionButton
          variant="ghost"
          size="sm"
          onClick={() => navigate({ to: "/admin-dpp" })}
        >
          <FiArrowLeft />
          <Text ml={2}>{t("common.backToOverview")}</Text>
        </VisionButton>
      </Flex>

      <Flex align="center" gap={3} mb={6} flexWrap="wrap">
        <IconBox variant="glass" size="lg">
          <FiLink fontSize="24px" color={sc.iconColor} />
        </IconBox>
        <Box flex="1">
          <Heading size="lg" color={sc.heading}>
            {t("bindings.title", "Tag Bindings")}
          </Heading>
          <Text color={sc.textMuted}>
            {t(
              "bindings.subtitle",
              "Which tags currently render a public DPP page, and which template they use.",
            )}
          </Text>
        </Box>
      </Flex>

      <VisionCard>
        <Flex
          justify="space-between"
          align="center"
          p={4}
          gap={3}
          flexWrap="wrap"
        >
          <Heading size="md" color={sc.heading}>
            {totalCount === 0
              ? t("bindings.listTitleEmpty", "No tags")
              : t(
                  "bindings.listTitleRange",
                  "Showing {{start}}–{{end}} of {{total}}",
                  { start: rangeStart, end: rangeEnd, total: totalCount },
                )}
          </Heading>

          <Flex gap={2} align="center" flexWrap="wrap">
            <Input
              size="sm"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t(
                "bindings.searchPlaceholder",
                "Search name or EPC",
              )}
              maxW="240px"
            />
            <select
              value={bindingFilter}
              onChange={(e) => setBindingFilter(e.target.value)}
              aria-label={t("bindings.filter.label", "Filter by binding")}
              style={{
                padding: "6px 8px",
                borderRadius: 8,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.06)",
                color: "inherit",
                minWidth: 200,
              }}
            >
              <option value="" style={{ background: "#1a202c" }}>
                {t("bindings.filter.all", "All tags")}
              </option>
              <option value="unbound" style={{ background: "#1a202c" }}>
                {t("bindings.filter.unbound", "Unbound only")}
              </option>
              {templates.map((tpl) => (
                <option
                  key={tpl.id}
                  value={tpl.id}
                  style={{ background: "#1a202c" }}
                >
                  {t("bindings.filter.boundTo", "Bound to: {{name}}", {
                    name: tpl.name,
                  })}
                </option>
              ))}
            </select>
          </Flex>

          {selectedTagIds.size > 0 && (
            <Flex gap={2} align="center" flexWrap="wrap">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("bindings.bulk.label", "Bind {{count}} selected to", {
                  count: selectedTagIds.size,
                })}
              </Text>
              <select
                value={bulkTemplateId}
                onChange={(e) => setBulkTemplateId(e.target.value)}
                disabled={isBusy}
                style={{
                  padding: "6px 8px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                  minWidth: 220,
                }}
              >
                <option value="" style={{ background: "#1a202c" }}>
                  {t("bindings.bulk.pickTemplate", "— pick a template —")}
                </option>
                {templates.map((tpl) => (
                  <option
                    key={tpl.id}
                    value={tpl.id}
                    style={{ background: "#1a202c" }}
                  >
                    {tpl.name}
                  </option>
                ))}
              </select>
              <VisionButton
                variant="brand"
                size="sm"
                disabled={!bulkTemplateId || isBusy}
                onClick={() => {
                  bindMutation.mutate({
                    tagIds: Array.from(selectedTagIds),
                    templateId: bulkTemplateId,
                    currentByTagId,
                  })
                  setPreviewTemplateId(bulkTemplateId)
                  setBulkTemplateId("")
                }}
              >
                {t("bindings.bulk.apply", "Apply")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={isBusy}
                onClick={() => {
                  bindMutation.mutate({
                    tagIds: Array.from(selectedTagIds),
                    templateId: null,
                    currentByTagId,
                  })
                  setPreviewTemplateId(null)
                }}
              >
                {t("bindings.bulk.unbind", "Unbind selected")}
              </VisionButton>
            </Flex>
          )}
        </Flex>

        {tagsQuery.isLoading ? (
          <Flex justify="center" py={6}>
            <Spinner size="md" color={sc.spinnerColor} />
          </Flex>
        ) : rows.length === 0 ? (
          <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
            {t("bindings.empty", "No tags in this organisation.")}
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader w="40px">
                    <Checkbox.Root
                      checked={allSelected}
                      onCheckedChange={(d) => toggleAllSelected(!!d.checked)}
                      size="sm"
                      aria-label="Select all tags"
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("bindings.col.tag", "Tag")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("bindings.col.sku", "SKU")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("bindings.col.dppTemplate", "DPP Template")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("bindings.col.locked", "Locked")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader color={sc.textMuted}>
                    {t("bindings.col.publicUrl", "Public URL")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((tag: any) => {
                  const tpl = tag.dpp_template_id
                    ? templateMap.get(tag.dpp_template_id)
                    : undefined
                  const publicUrl = tag.url
                    ? tag.url
                    : dppHost
                      ? `https://${dppHost}/${tag.epcid}`
                      : null
                  const isSelected = selectedTagIds.has(tag.id)
                  return (
                    <Table.Row key={tag.id}>
                      <Table.Cell>
                        <Checkbox.Root
                          checked={isSelected}
                          onCheckedChange={(d) =>
                            toggleTagSelection(tag.id, !!d.checked)
                          }
                          size="sm"
                          aria-label={`Select ${tag.name}`}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell color={sc.heading} fontSize="sm">
                        <Box>
                          <Text fontWeight="600">{tag.name}</Text>
                          <Text
                            fontSize="xs"
                            color={sc.textMuted}
                            fontFamily="mono"
                          >
                            {tag.epcid}
                          </Text>
                        </Box>
                      </Table.Cell>
                      <Table.Cell>
                        {/*
                         * SKU pulldown sourced from the catalog. The
                         * SKU drives DPP record resolution at render
                         * time (Tag.sku → DppRecord.gs1_sku). Add a
                         * new SKU via /tag-sku-editor before assigning
                         * it here.
                         */}
                        <select
                          value={tag.sku ?? ""}
                          disabled={isBusy}
                          onChange={(e) => {
                            const next = e.target.value
                            if ((tag.sku ?? "") === next) return
                            updateSkuMutation.mutate({
                              tagId: tag.id,
                              sku: next,
                            })
                          }}
                          style={{
                            width: "100%",
                            maxWidth: 200,
                            padding: "4px 6px",
                            borderRadius: 6,
                            border: "1px solid rgba(255,255,255,0.12)",
                            background: "rgba(255,255,255,0.06)",
                            color: "inherit",
                            fontFamily: "monospace",
                          }}
                          aria-label={`SKU for ${tag.name}`}
                        >
                          <option value="" style={{ background: "#1a202c" }}>
                            {t("bindings.noSku", "— (none) —")}
                          </option>
                          {tag.sku && !skuOptions.includes(tag.sku) && (
                            <option
                              value={tag.sku}
                              style={{ background: "#1a202c" }}
                            >
                              {tag.sku} (legacy)
                            </option>
                          )}
                          {skuOptions.map((opt) => (
                            <option
                              key={opt}
                              value={opt}
                              style={{ background: "#1a202c" }}
                            >
                              {opt}
                            </option>
                          ))}
                        </select>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap={2} align="center" flexWrap="wrap">
                          <select
                            value={tag.dpp_template_id ?? ""}
                            disabled={isBusy}
                            onChange={(e) => {
                              const next = e.target.value || null
                              bindMutation.mutate({
                                tagIds: [tag.id],
                                templateId: next,
                                currentByTagId,
                              })
                              setPreviewTemplateId(next)
                            }}
                            style={{
                              padding: "4px 6px",
                              borderRadius: 6,
                              border: "1px solid rgba(255,255,255,0.12)",
                              background: "rgba(255,255,255,0.06)",
                              color: "inherit",
                              minWidth: 180,
                            }}
                            aria-label={`Template for ${tag.name}`}
                          >
                            <option value="" style={{ background: "#1a202c" }}>
                              {t("bindings.unboundOption", "— Unbound —")}
                            </option>
                            {templates.map((opt) => (
                              <option
                                key={opt.id}
                                value={opt.id}
                                style={{ background: "#1a202c" }}
                              >
                                {opt.name}
                              </option>
                            ))}
                          </select>
                          {tpl && !tpl.enabled && (
                            <Badge colorPalette="orange" size="sm">
                              {t("bindings.templateDisabled", "disabled")}
                            </Badge>
                          )}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Checkbox.Root
                          checked={!!tag.dpp_binding_locked}
                          disabled={isBusy}
                          onCheckedChange={(d) =>
                            bindMutation.mutate({
                              tagIds: [tag.id],
                              templateId: tag.dpp_template_id ?? null,
                              locked: !!d.checked,
                              currentByTagId,
                            })
                          }
                          size="sm"
                          aria-label={`Lock binding for ${tag.name}`}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell
                        color={sc.heading}
                        fontSize="xs"
                        fontFamily="mono"
                      >
                        {publicUrl ? (
                          <a
                            href={publicUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: "underline" }}
                          >
                            {publicUrl}
                          </a>
                        ) : (
                          "—"
                        )}
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table.Root>
          </Box>
        )}

        {totalCount > 0 && (
          <Flex
            justify="space-between"
            align="center"
            p={4}
            gap={3}
            flexWrap="wrap"
            borderTop="1px solid"
            borderColor={sc.border}
          >
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("bindings.pagination.pageSize", "Rows per page")}
              </Text>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(0)
                }}
                style={{
                  padding: "4px 6px",
                  borderRadius: 6,
                  border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.06)",
                  color: "inherit",
                }}
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n} style={{ background: "#1a202c" }}>
                    {n}
                  </option>
                ))}
              </select>
            </Flex>
            <Flex gap={2} align="center">
              <Text fontSize="sm" color={sc.textMuted}>
                {t("bindings.pagination.pageOf", "Page {{page}} of {{count}}", {
                  page: page + 1,
                  count: pageCount,
                })}
              </Text>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(0)}
              >
                {t("bindings.pagination.first", "« First")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                {t("bindings.pagination.prev", "‹ Prev")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() =>
                  setPage((p) => Math.min(pageCount - 1, p + 1))
                }
              >
                {t("bindings.pagination.next", "Next ›")}
              </VisionButton>
              <VisionButton
                variant="ghost"
                size="sm"
                disabled={page >= pageCount - 1}
                onClick={() => setPage(pageCount - 1)}
              >
                {t("bindings.pagination.last", "Last »")}
              </VisionButton>
            </Flex>
          </Flex>
        )}
      </VisionCard>

      {previewTemplateId &&
        (() => {
          const previewTpl = templateMap.get(previewTemplateId) as
            | (PassportTemplateRead & { rules?: any[] })
            | undefined
          if (!previewTpl) return null
          const rules: any[] = Array.isArray(previewTpl.rules)
            ? previewTpl.rules
            : []
          return (
            <VisionCard mt={5}>
              <Flex
                justify="space-between"
                align="center"
                p={4}
                gap={3}
                flexWrap="wrap"
              >
                <Box>
                  <Heading size="md" color={sc.heading}>
                    {t("bindings.preview.title", "Rules — {{name}}", {
                      name: previewTpl.name,
                    })}
                  </Heading>
                  <Text fontSize="xs" color={sc.textMuted} mt={1}>
                    {t(
                      "bindings.preview.caption",
                      "Evaluation order: rules are walked top to bottom; among matching rules, geo wins over time. This pane is a display-only preview of the last template you bound to.",
                    )}
                  </Text>
                </Box>
                <VisionButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setPreviewTemplateId(null)}
                >
                  {t("bindings.preview.close", "Close")}
                </VisionButton>
              </Flex>
              {rules.length === 0 ? (
                <Text color={sc.textMuted} fontSize="sm" px={4} pb={4}>
                  {t("bindings.preview.noRules", "This template has no rules.")}
                </Text>
              ) : (
                <Box px={4} pb={4}>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader color={sc.textMuted} w="40px">
                          #
                        </Table.ColumnHeader>
                        <Table.ColumnHeader color={sc.textMuted}>
                          {t("bindings.preview.col.type", "Type")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader color={sc.textMuted}>
                          {t("bindings.preview.col.params", "Parameters")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader color={sc.textMuted}>
                          {t("bindings.preview.col.redirect", "Redirect URL")}
                        </Table.ColumnHeader>
                        <Table.ColumnHeader color={sc.textMuted}>
                          {t("bindings.preview.col.enabled", "Enabled")}
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rules.map((r, i) => {
                        const hasGeo =
                          r &&
                          typeof r === "object" &&
                          r.geo &&
                          typeof r.geo === "object"
                        const hasTime =
                          r &&
                          typeof r === "object" &&
                          r.time &&
                          typeof r.time === "object"
                        const typeLabel =
                          hasGeo && hasTime
                            ? t("bindings.preview.type.both", "geo + time")
                            : hasGeo
                              ? t("bindings.preview.type.geo", "geo")
                              : hasTime
                                ? t("bindings.preview.type.time", "time")
                                : t("bindings.preview.type.invalid", "invalid")
                        const parts: string[] = []
                        if (hasGeo) {
                          parts.push(
                            `lat=${r.geo.lat}, lon=${r.geo.lon}, r=${r.geo.radius_m}m`,
                          )
                        }
                        if (hasTime) {
                          parts.push(
                            `after ${r.time.after_amount} ${r.time.after_unit}`,
                          )
                        }
                        return (
                          <Table.Row key={i}>
                            <Table.Cell color={sc.textMuted} fontSize="xs">
                              {i + 1}
                              {r?.label ? (
                                <Text
                                  fontSize="xs"
                                  color={sc.textMuted}
                                  mt={0.5}
                                >
                                  {r.label}
                                </Text>
                              ) : null}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                colorPalette={
                                  hasGeo && hasTime
                                    ? "purple"
                                    : hasGeo
                                      ? "blue"
                                      : hasTime
                                        ? "teal"
                                        : "gray"
                                }
                                size="sm"
                              >
                                {typeLabel}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell
                              color={sc.heading}
                              fontSize="xs"
                              fontFamily="mono"
                            >
                              {parts.join("  ·  ") || "—"}
                            </Table.Cell>
                            <Table.Cell
                              color={sc.heading}
                              fontSize="xs"
                              fontFamily="mono"
                            >
                              {r?.redirect_url ? (
                                <>
                                  {String(r.redirect_url).replace(/\/+$/, "")}
                                  <Text as="span" color={sc.textMuted}>
                                    {"/<EPC>"}
                                  </Text>
                                </>
                              ) : (
                                "—"
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge
                                colorPalette={
                                  r?.enabled === false ? "gray" : "green"
                                }
                                size="sm"
                              >
                                {r?.enabled === false
                                  ? t("bindings.preview.off", "off")
                                  : t("bindings.preview.on", "on")}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        )
                      })}
                    </Table.Body>
                  </Table.Root>
                </Box>
              )}
            </VisionCard>
          )
        })()}
    </Box>
  )
}
